/**
 * Continue fresh from the drawer (ADR 0062 decision 2a): what Orgus offers before resuming a
 * session, and the action that continues its mission in a fresh session from the handoff.
 *
 *   offer     GET  /api/missions/continuation?sessionId=  (demo: from the local comparison)
 *   continue  POST /api/missions/:id/continue-fresh       { sessionId }
 */
import { useCallback, useEffect, useState } from 'react';
import type { ContinuationCost, ContinuationOffer, Session } from '@orgus/protocol';
import { freshWins, sessionLiveUsage } from '@orgus/protocol';
import { apiGet, apiPost, toUiError } from '../../lib/api.ts';
import type { UiError } from '../../lib/api.ts';
import { useOrgus } from '../../lib/store.ts';

/** The demo offer: a mission's idle session where starting fresh wins, its handoff written. */
export function demoOffer(s: Pick<Session, 'id' | 'status' | 'origin'>, c: ContinuationCost | null): ContinuationOffer | null {
  if (!c || s.status === 'working' || s.origin !== 'orgus' || !freshWins(c) || !(c.cold || c.forced)) return null;
  return { sessionId: s.id, missionId: 'msn_demo', continueUsd: c.continue, freshUsd: c.fresh, saving: c.continue - c.fresh, remainingRequests: c.remainingRequests, reason: c.reason, handoff: 'ready', auto: false };
}

/** Is it worth asking the hub: the session waits and the local comparison does not say continue. */
export function worthAsking(s: Pick<Session, 'status'>, c: ContinuationCost | null): boolean {
  return !!c && s.status !== 'working' && c.recommended !== 'continue';
}

export interface OfferData {
  offer: ContinuationOffer | null;
  error: UiError | null;
  reload(): void;
}

export function useContinuationOffer(s: Session, c: ContinuationCost | null): OfferData {
  const mode = useOrgus((st) => st.mode);
  const [offer, setOffer] = useState<ContinuationOffer | null>(null);
  const [error, setError] = useState<UiError | null>(null);
  const [tick, setTick] = useState(0);
  const ask = mode === 'live' && worthAsking(s, c);
  const lastRequestAt = sessionLiveUsage(s)?.lastRequestAt ?? null;
  const cold = c?.cold ?? false;
  useEffect(() => {
    if (!ask) {
      setOffer(null);
      return;
    }
    let alive = true;
    apiGet<{ offer: ContinuationOffer | null }>(`/api/missions/continuation?sessionId=${encodeURIComponent(s.id)}`)
      .then((r) => {
        if (!alive) return;
        setOffer(r.offer);
        setError(null);
      })
      .catch((e) => alive && setError(toUiError(e, { what: 'Could not compare continuing with starting fresh.' })));
    return () => {
      alive = false;
    };
  }, [ask, s.id, s.status, lastRequestAt, cold, tick]);
  const reload = useCallback(() => setTick((n) => n + 1), []);
  if (mode === 'mock') return { offer: demoOffer(s, c), error: null, reload };
  return { offer, error, reload };
}

/** Continue the session's mission in a fresh session from its handoff. Throws a UiError. */
export async function continueFresh(offer: ContinuationOffer): Promise<void> {
  if (useOrgus.getState().mode === 'mock' || !offer.missionId) return;
  try {
    await apiPost(`/api/missions/${encodeURIComponent(offer.missionId)}/continue-fresh`, { sessionId: offer.sessionId });
  } catch (e) {
    throw toUiError(e, { what: 'The session did not continue fresh.' });
  }
}
